/**
 * Un routeur de trois cents octets, parce que cinq écrans n'en demandent pas
 * davantage.
 *
 * Il écoute `popstate` — les boutons *précédent* et *suivant* du navigateur —
 * et expose `naviguer()` pour les liens internes. Une bibliothèque de routage
 * apporterait les routes imbriquées, les chargeurs et les transitions ; rien
 * de tout cela n'est employé ici, et chaque dépendance est une montée de
 * version à suivre.
 *
 * Les liens profonds fonctionnent au **rechargement** parce que le portail
 * sert `index.html` pour tout chemin inconnu : sans cette route attrape-tout,
 * `/affaires/…` rendrait 404 dès qu'on actualise la page.
 */
import { useCallback, useEffect, useState } from "react";

/** Va quelque part.
 *
 * **`remplacer` n'est pas un détail de confort.** Le champ de recherche
 * naviguait à chaque frappe : taper « 30429 » laissait cinq crans dans
 * l'historique, et *précédent* remontait la recherche lettre à lettre au lieu
 * de quitter la liste. Une destination s'empile ; un affinage remplace. */
export function naviguer(vers: string, remplacer = false): void {
  if (vers === window.location.pathname + window.location.search) return;
  if (remplacer) window.history.replaceState({}, "", vers);
  else window.history.pushState({}, "", vers);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useChemin(): string {
  const [chemin, poser] = useState(() => window.location.pathname);
  useEffect(() => {
    const suivre = () => poser(window.location.pathname);
    window.addEventListener("popstate", suivre);
    return () => window.removeEventListener("popstate", suivre);
  }, []);
  return chemin;
}

/** La requête courante, suivie comme le chemin.
 *
 * `useChemin` ne regarde que `pathname` : une navigation qui ne change que la
 * requête — chercher « 30429 » depuis la liste déjà ouverte — ne rendait donc
 * rien. Le champ de recherche naviguait dans le vide. */
export function useRequete(): URLSearchParams {
  const [requete, poser] = useState(() => window.location.search);
  useEffect(() => {
    const suivre = () => poser(window.location.search);
    window.addEventListener("popstate", suivre);
    return () => window.removeEventListener("popstate", suivre);
  }, []);
  return new URLSearchParams(requete);
}

/** Un paramètre de la requête, lu et écrit dans l'adresse.
 *
 * **C'est ici que se règle ce qui survit à un rechargement.** L'onglet ouvert,
 * la catégorie filtrée, la pièce qu'on regarde : autant de « où l'on est »,
 * qui appartiennent à l'adresse. Gardés dans un `useState`, ils disparaissent
 * au premier F5 — et l'on retombe sur la synthèse d'une affaire dont on
 * lisait les documents.
 *
 * Ce qui n'y va **pas** : les réglages de confort, qui sont personnels et
 * n'ont rien à faire dans un lien envoyé à un collègue (ils vivent dans le
 * navigateur) ; et ce qu'on est en train de faire — une saisie, une requête
 * en vol, une confirmation de destruction — qu'il vaut mieux perdre que
 * ressusciter à moitié.
 *
 * La valeur vide efface le paramètre au lieu de l'écrire : `?cat=` ne dit rien
 * de plus que son absence, et allonge une adresse qu'on colle. */
export function useParametre(
  nom: string,
  defaut = "",
): [string, (valeur: string, remplacer?: boolean) => void] {
  const requete = useRequete();
  const valeur = requete.get(nom) ?? defaut;

  const poser = useCallback(
    (neuve: string, remplacer = false) => {
      const adresse = new URL(window.location.href);
      if (!neuve || neuve === defaut) adresse.searchParams.delete(nom);
      else adresse.searchParams.set(nom, neuve);
      naviguer(adresse.pathname + adresse.search, remplacer);
    },
    [nom, defaut],
  );

  return [valeur, poser];
}

/** Un lien interne : il navigue sans recharger, mais reste un vrai lien —
 * clic du milieu, « ouvrir dans un nouvel onglet » et survol continuent de
 * marcher, ce qu'un `<span onClick>` aurait cassé. */
export function Lien(
  props: { vers: string; children: React.ReactNode; className?: string },
) {
  const aller = useCallback(
    (evenement: React.MouseEvent) => {
      if (evenement.metaKey || evenement.ctrlKey || evenement.button !== 0) return;
      evenement.preventDefault();
      naviguer(props.vers);
    },
    [props.vers],
  );
  return (
    <a href={props.vers} onClick={aller} className={props.className}>
      {props.children}
    </a>
  );
}
